import { Link } from "./navigation";
import { useState, type ReactNode } from "react";
import { Menu, ChevronLeft, ChevronRight, X, ArrowUpRight, BookOpen, ClipboardList, LogOut, Users, Bookmark, LayoutDashboard, ClipboardCheck, ChartNoAxesColumn } from "lucide-react";
import { BrandMark } from "./BrandMark";
import type { Actor } from "./portal-data";

export function PortalShell({ actor, localPreview, active, children, onSignOut }: {
  actor: Actor;
  localPreview: boolean;
  active: string;
  children: ReactNode;
  onSignOut: () => void;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sat:sidebar-collapsed") === "true"; } catch { return false; }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  function toggleSidebar() {
    setCollapsed((value) => {
      try { localStorage.setItem("sat:sidebar-collapsed", String(!value)); } catch { /* Storage can be unavailable. */ }
      return !value;
    });
  }
  const admin = actor.role !== "learner";
  const links = admin ? [
    { href: "/admin/overview", label: "Overview", icon: LayoutDashboard },
    { href: "/admin/tests", label: "Tests", icon: BookOpen },
    { href: "/admin/assignments", label: "Assignments", icon: ClipboardList },
    { href: "/admin/reviews", label: "Review work", icon: ClipboardCheck },
    { href: "/admin/students", label: "Students", icon: Users }
  ] : [
    { href: "/student/tests", label: "Tests", icon: BookOpen },
    { href: "/student/study", label: "Study", icon: Bookmark },
    { href: "/student/progress", label: "Progress", icon: ChartNoAxesColumn }
  ];

  return (
    <div className={`portal ${admin ? "admin-portal" : "student-portal"} ${collapsed ? "sidebar-collapsed" : ""} ${menuOpen ? "menu-open" : ""}`}>
      <a className="portal-skip-link" href="#portal-main">Skip to content</a>
      <aside className="portal-sidebar" onKeyDown={(event) => { if (event.key === "Escape") { setMenuOpen(false); document.getElementById("portal-menu-toggle")?.focus(); } }}>
        <div className="sidebar-head">
          <Link className="portal-brand" href={links[0].href} aria-label={`100prep ${admin ? "Admin" : "Student"}`}>
            <span className="portal-brand-mark"><BrandMark /></span>
            <span className="brand-copy">
              <strong>100prep</strong>
              <em>{admin ? "Admin" : "Digital SAT"}</em>
            </span>
          </Link>
          <button className="sidebar-collapse" onClick={toggleSidebar} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!collapsed} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>{collapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}</button>
        </div>
        {admin ? <button id="portal-menu-toggle" className="mobile-menu-toggle" aria-label={menuOpen ? "Close navigation" : "Open navigation"} aria-expanded={menuOpen} aria-controls="portal-navigation" onClick={() => setMenuOpen((open) => !open)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button> : localPreview ? <Link className="mobile-account-action" href="/admin/tests" aria-label="Back to admin"><ArrowUpRight size={18} /></Link> : <button className="mobile-account-action" type="button" onClick={onSignOut} aria-label="Sign out"><LogOut size={18} /></button>}
        {admin && <div className="portal-role">Administration</div>}
        <nav id="portal-navigation" onClick={(event) => { if ((event.target as HTMLElement).closest("a")) setMenuOpen(false); }} aria-label={admin ? "Admin navigation" : "Student navigation"}>
          {links.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              title={collapsed ? label : undefined}
              aria-label={label}
              className={active === href ? "active" : ""}
              aria-current={active === href ? "page" : undefined}
            >
              <Icon size={18} strokeWidth={1.7} />
              <span>{label}</span>
            </Link>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {localPreview && (
            <Link aria-label={admin ? "Switch to test student" : "Back to admin"} title={admin ? "Switch to test student" : "Back to admin"} className="preview-switch" href={admin ? "/student/tests" : "/admin/tests"}>
              <div>
                <span>{admin ? "Switch to test student" : "Back to admin"}</span>
                <small>Local preview</small>
              </div>
              <ArrowUpRight size={15} />
            </Link>
          )}
          <div className="portal-account">
            <span className="account-avatar">{actor.name.slice(0, 1).toUpperCase()}</span>
            <div className="account-copy">
              <strong>{localPreview ? (admin ? "Local admin" : "Test student") : actor.name}</strong>
              <small>{actor.email}</small>
            </div>
            {!localPreview && (
              <button className="account-signout" type="button" onClick={onSignOut} aria-label="Sign out">
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
      </aside>
      <main className="portal-main" id="portal-main" tabIndex={-1}>
        {localPreview && (
          <div className="local-preview-strip">
            <span className="preview-indicator" />
            <span>Local preview active</span>
            <span className="preview-account-tag">{admin ? "Admin account" : "Test student account"}</span>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

export function PageHeading({ eyebrow, title, children }: { eyebrow?: string; title: string; children?: ReactNode }) {
  return (
    <header className="portal-heading">
      <div className="heading-titles">
        {eyebrow && <p className="page-kicker">{eyebrow}</p>}
        <h1 className="page-title" tabIndex={-1}>{title}</h1>
      </div>
      {children && <div className="heading-actions">{children}</div>}
    </header>
  );
}

export function RequestState({ loading, error, retry }: { loading: boolean; error: string; retry: () => void }) {
  return (
    <>
      {loading && <div className="loading-line" role="status" aria-label="Loading" />}
      {error && (
        <div className="portal-error" role="alert">
          <p>{error}</p>
          <button className="secondary-button" disabled={loading} onClick={retry}>Retry</button>
        </div>
      )}
    </>
  );
}
